import { IEntityFieldType } from '../../types/entities/IEntityFieldType';
import {
    AttributeDefinitionFieldType,
    AttributeDefinitionSizeKeyType,
    AttributeDefinitionState,
    AttributeValueState,
} from '../../types/properties/propertyTypes';
import {
    doubleMD_ColumnGrid,
    full_ColumnGrid,
    singleLG_DoubleMD_ColumnGrid,
} from '../commonVariables/grid';
import variables from '../../stylesheets/variables.module.scss';

export const getFieldType = (
    fieldType: Partial<IEntityFieldType>
): AttributeDefinitionFieldType => {
    switch (true) {
        case fieldType.IsBoolean:
            return AttributeDefinitionFieldType.BOOLEAN;
        case fieldType.IsDate:
            return AttributeDefinitionFieldType.DATE;
        case fieldType.IsNumeric:
            return AttributeDefinitionFieldType.NUMBER;
        default:
            return AttributeDefinitionFieldType.STRING;
    }
};

export const getGridChildSize = (fieldSize: AttributeDefinitionSizeKeyType) => {
    switch (fieldSize) {
        case AttributeDefinitionSizeKeyType.SMALL:
            return singleLG_DoubleMD_ColumnGrid;
        case AttributeDefinitionSizeKeyType.MEDIUM:
            return doubleMD_ColumnGrid;
        case AttributeDefinitionSizeKeyType.LARGE:
            return full_ColumnGrid;
        default:
            return singleLG_DoubleMD_ColumnGrid;
    }
};

export const getDefaultValue = (
    fieldType: AttributeDefinitionFieldType
): string => {
    switch (fieldType) {
        case AttributeDefinitionFieldType.BOOLEAN:
            return 'False';
        case AttributeDefinitionFieldType.DATE:
            return new Date().toISOString();
        case AttributeDefinitionFieldType.NUMBER:
            return '0';
        default:
            return '';
    }
};

const getFieldTypeSortOrder = (fieldType: AttributeDefinitionFieldType) => {
    switch (fieldType) {
        case AttributeDefinitionFieldType.BOOLEAN:
            return 0;
        case AttributeDefinitionFieldType.STRING:
            return 1;
        case AttributeDefinitionFieldType.NUMBER:
            return 2;
        case AttributeDefinitionFieldType.DATE:
            return 3;

        default:
            return 99;
    }
};

export const sortAttributeDefinitions = (
    attributeDefinitions: AttributeDefinitionState[]
) => {
    return [...attributeDefinitions].sort((a, b) => {
        if (a.unique !== b.unique) {
            return a.unique ? -1 : 1;
        }

        const fieldTypeComparison =
            getFieldTypeSortOrder(a.fieldType) -
            getFieldTypeSortOrder(b.fieldType);

        if (fieldTypeComparison !== 0) {
            return fieldTypeComparison;
        }

        return a.name.localeCompare(b.name);
    });
};

export const zitoneoServiceAttributesColors = [
    variables.accentColorCyan400,
    // variables.accentColorYellow400,
    // variables.accentColorOrange400,
    // variables.accentColorPink400,
    variables.accentColorDarkBlue400,
    variables.accentColorPurple400,
];

export const hasAttributeValue = (value: AttributeValueState['value']) => {
    if (typeof value === 'boolean') {
        return true;
    }

    if (typeof value === 'number') {
        return value;
    }

    if (value instanceof Date) {
        return !Number.isNaN(value.getTime());
    }

    if (value === null || value === undefined) {
        return false;
    }

    return !!String(value).trim();
};

export const getAttributesIncompleteSummary = (
    attributeDefinitions?: AttributeDefinitionState[],
    attributeValues?: AttributeValueState[]
) => {
    if (!attributeDefinitions?.length) {
        return {
            incompleteCount: 0,
            requiredIncompleteCount: 0,
        };
    }

    return attributeDefinitions.reduce(
        (accumulator, definition) => {
            const definitionValues = (attributeValues || []).filter(
                value =>
                    value.idAttributeDefinition ===
                        definition.idAttributeDefinition && !value.Deleted
            );

            const hasAnyValue = definitionValues.some(value =>
                hasAttributeValue(value.value)
            );

            if (!hasAnyValue) {
                accumulator.incompleteCount += 1;

                if (definition.required) {
                    accumulator.requiredIncompleteCount += 1;
                }
            }

            return accumulator;
        },
        {
            incompleteCount: 0,
            requiredIncompleteCount: 0,
        }
    );
};
